/**
 * De naam van de bewoner zoals de tablet hem toont (specs/20261025-import-bewoners-eigenaars,
 * IM-22).
 *
 * Een placeholder ("Bewoner N", aangemaakt door de import) is geen echte bewoner: de tablet
 * toont dan "Onbewoond" in de taal van de app. De API beslist wat een placeholder is
 * (`utils/placeholderPerson.js` → `isPlaceholder` op het profiel, of `person.isPlaceholder`
 * na een login); de app rekent het NIET zelf na.
 *
 * ALLEEN WEERGAVE. Noodknop, bellen, videofoon en meldingen werken op een placeholder-tablet
 * gewoon door — gebruik `isPlaceholderUser` nooit om een functie uit te schakelen.
 */

/**
 * Zegt de API dat deze sessie bij een placeholder-persoon hoort? De vlag van het profiel
 * (`/user/profile`, toestel-token) wint: `profile_refresh` voegt `person` samen i.p.v. te
 * vervangen, dus een `person.isPlaceholder` van een oude login kan blijven hangen.
 */
export function isPlaceholderUser(user: any): boolean {
  if (typeof user?.isPlaceholder === 'boolean') return user.isPlaceholder;
  return user?.person?.isPlaceholder === true;
}

/**
 * IM-24: een koppel deelt één tablet, die aan één van beiden hangt. De API geeft de andere
 * echte bewoners van de unit mee als `coResidents` (`/user/profile`, toestel-token); de tablet
 * toont dan beide namen. Een placeholder-sessie met een echte medebewoner toont die bewoner.
 */
interface NamedPerson { firstName?: string | null; lastName?: string | null; friendlyName?: string | null }

function coResidents(user: any): NamedPerson[] {
  return Array.isArray(user?.coResidents) ? user.coResidents.filter(Boolean) : [];
}

/** Wie er op deze tablet woont: de eigen persoon (tenzij placeholder) + de medebewoners. */
function residentsOf(user: any): NamedPerson[] {
  const self: NamedPerson[] = user && !isPlaceholderUser(user) ? [user] : [];
  return [...self, ...coResidents(user)];
}

/** "A", "A & B", "A, B & C". */
export function joinNames(names: string[]): string {
  const list = names.map((n) => n.trim()).filter(Boolean);
  if (list.length <= 1) return list[0] || '';
  return `${list.slice(0, -1).join(', ')} & ${list[list.length - 1]}`;
}

const first = (p: NamedPerson) => (p.firstName || p.friendlyName || '').trim();
const last = (p: NamedPerson) => (p.lastName || '').trim();

/**
 * Wie de tablet aanspreekt (weergave-instelling `showResidentName`, per site of appartement).
 *
 *   uit (standaard)  altijd het algemene woord in de taal van de tablet ("Goedemorgen
 *                    bewoner") — ook bij een placeholder. Een site die geen persoonsgegevens
 *                    met ons deelt, heeft overal placeholders: "Onbewoond" zou daar liegen.
 *   aan              de voornaam(en); een placeholder zonder medebewoner → "Onbewoond" (IM-22).
 */
export function greetingName(user: any, opts: { byName: boolean; resident: string; unoccupied: string }): string {
  if (!opts.byName) return opts.resident;
  return residentFirstName(user, opts.unoccupied, opts.resident);
}

/** Volledige naam voor Instellingen; uit → het algemene woord met hoofdletter. */
export function displayFullName(user: any, opts: { byName: boolean; resident: string; unoccupied: string }): string {
  if (!opts.byName) return opts.resident.charAt(0).toUpperCase() + opts.resident.slice(1);
  return residentFullName(user, opts.unoccupied);
}

/** Voornaam voor de begroeting ("Christophe & Els"); geen bewoner → `unoccupied`, geen naam → `fallback`. */
export function residentFirstName(user: any, unoccupied: string, fallback = ''): string {
  const people = residentsOf(user);
  if (!people.length) return user && isPlaceholderUser(user) ? unoccupied : fallback;
  if (people.length === 1 && people[0] === user) return user?.firstName || fallback;
  return joinNames(people.map(first)) || fallback;
}

/**
 * Voor- + achternaam van de sessie; geen bewoner → `unoccupied`. Een koppel met dezelfde
 * achternaam: "Jan & Maria Peeters", anders "Christophe Demeulemeester & Els Voorbeeld".
 */
export function residentFullName(user: any, unoccupied: string): string {
  const people = residentsOf(user);
  if (!people.length) return user && isPlaceholderUser(user) ? unoccupied : '';
  if (people.length === 1) return `${first(people[0])} ${last(people[0])}`.trim();
  const lastNames = new Set(people.map(last));
  if (lastNames.size === 1 && last(people[0])) return `${joinNames(people.map(first))} ${last(people[0])}`;
  return joinNames(people.map((p) => `${first(p)} ${last(p)}`.trim()));
}
